import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
} from "react";
import productService from "../service/productService";
import { useTheme, useMediaQuery } from "@mui/material";
import { normalizeRusticMediaProduct } from "../utils/mediaUrl";

const initialState = {
  page: 1,
  categories: [],
  priceOrder: "asc",
  namePrefix: "",
  selectedProduct: null,
};

export function useProductLimit() {
  const theme = useTheme();

  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isLg = useMediaQuery(theme.breakpoints.between("lg", "xl"));
  const isXL = useMediaQuery(theme.breakpoints.up("xl"));
  if (isXs) return 1;
  if (isSm) return 2;
  if (isMd) return 3;
  if (isLg) return 4;

  if (isXL) return 5;
  return 1;
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_PAGE":
      return { ...state, page: action.payload };
    case "SET_CATEGORIES":
      return { ...state, categories: action.payload, page: 1 };
    case "SET_PRICE_ORDER":
      return { ...state, priceOrder: action.payload, page: 1 };
    case "SET_NAME_PREFIX":
      return { ...state, namePrefix: action.payload, page: 1 };
    case "SET_SKU_PREFIX":
      return { ...state, skuPrefix: action.payload, page: 1 };
    case "SET_SELECTED_PRODUCT":
      return { ...state, selectedProduct: action.payload };
    case "CLEAR_SELECTED_PRODUCT":
      return { ...state, selectedProduct: null };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

const init = () => {
  const stored = localStorage.getItem("productFilters");
  return stored ? JSON.parse(stored) : initialState;
};

const ProductFilterContext = createContext();

export function ProductFilterProvider({ children }) {
  const limit = useProductLimit();
  const [state, dispatch] = useReducer(reducer, initialState, init);
  const [products, setProducts] = useState([]);
  const [pages, setPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem("productFilters", JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    setLoading(true);
    productService
      .getProductsPaginated(
        state.categories,
        state.priceOrder,
        limit,
        state.page,
        state.namePrefix,
        state.skuPrefix,
      )
      .then((data) => {
        setProducts((data.products || []).map(normalizeRusticMediaProduct));
        setPages(data.pages > 0 ? data.pages : 1);
        setTotalProducts(data.count || 0);
      })
      .catch((err) => console.error("Error fetching products:", err))
      .finally(() => setLoading(false));
  }, [state, limit]);

  useEffect(() => {
    productService
      .getCategories()
      .then((data) => setAllCategories(data || []))
      .catch((err) => console.error("Error fetching categories:", err));
  }, []);

  const setPage = (page) => dispatch({ type: "SET_PAGE", payload: page });
  const setCategories = (categories) =>
    dispatch({ type: "SET_CATEGORIES", payload: categories });
  const setPriceOrder = (priceOrder) =>
    dispatch({ type: "SET_PRICE_ORDER", payload: priceOrder });
  const setNamePrefix = (prefix) =>
    dispatch({ type: "SET_NAME_PREFIX", payload: prefix });
  const setSkuPrefix = (prefix) =>
    dispatch({ type: "SET_SKU_PREFIX", payload: prefix });
  const resetFilters = () => dispatch({ type: "RESET" });
  const setSelectedProduct = (product) =>
    dispatch({ type: "SET_SELECTED_PRODUCT", payload: product });

  const clearSelectedProduct = () =>
    dispatch({ type: "CLEAR_SELECTED_PRODUCT" });

  return (
    <ProductFilterContext.Provider
      value={{
        ...state,
        setPage,
        setCategories,
        setPriceOrder,
        setNamePrefix,
        setSkuPrefix,
        resetFilters,
        setSelectedProduct,
        clearSelectedProduct,
        products,
        pages,
        totalProducts,
        allCategories,
        loading,
      }}
    >
      {children}
    </ProductFilterContext.Provider>
  );
}

export const useProductFilters = () => useContext(ProductFilterContext);
